import { useMemo } from 'react';
import { FormControl, InputLabel, Select, TextField, makeStyles } from '@material-ui/core';

import {
  ENTITY_STATUS_FILTERS,
  ENTITY_STATUS_FILTER_LABELS,
  EntityStatusFilter,
} from '../../../shared/constants/entity-status-filter.constants';
import { GetSegmentsRequest, useAdminGetSegments } from '../../../segments/hooks/admin-get-segments.hook';
import { DeliveryPointSegment } from '../../types/delivery-point.type';
import {
  ALL_DELIVERY_POINT_SEGMENTS,
  ALL_DELIVERY_POINT_TYPES,
  DELIVERY_POINT_TYPES,
  DELIVERY_POINT_TYPE_LABELS,
  DeliveryPointTypeFilter,
} from '../../constants/delivery-point-type.constants';

const SEARCH_INPUT_ID = 'delivery-points-search';
const TYPE_FILTER_INPUT_ID = 'delivery-points-type-filter';
const SEGMENT_FILTER_INPUT_ID = 'delivery-points-segment-filter';
const STATUS_FILTER_INPUT_ID = 'delivery-points-status-filter';

const ACTIVE_SEGMENTS_REQUEST: GetSegmentsRequest = { page: 1, limit: 100, filters: { isActive: true } };

interface DeliveryPointsListFiltersProps {
  search: string;
  typeFilter: DeliveryPointTypeFilter;
  segmentFilter: string;
  statusFilter: EntityStatusFilter;
  onSearchChange: (value: string) => void;
  onTypeFilterChange: (value: DeliveryPointTypeFilter) => void;
  onSegmentFilterChange: (value: string) => void;
  onStatusFilterChange: (value: EntityStatusFilter) => void;
}

const useStyles = makeStyles({
  filters: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
  },
});

const groupSegmentsByLetter = (segments: DeliveryPointSegment[]): [string, DeliveryPointSegment[]][] => {
  const sortedSegments = [...segments].sort((first, second) => first.name.localeCompare(second.name, 'fr'));
  const groups = new Map<string, DeliveryPointSegment[]>();

  sortedSegments.forEach((segment) => {
    const letter = segment.name.charAt(0).toUpperCase();
    groups.set(letter, [...(groups.get(letter) ?? []), segment]);
  });

  return Array.from(groups.entries());
};

export function DeliveryPointsListFilters({
  search,
  typeFilter,
  segmentFilter,
  statusFilter,
  onSearchChange,
  onTypeFilterChange,
  onSegmentFilterChange,
  onStatusFilterChange,
}: DeliveryPointsListFiltersProps) {
  const classes = useStyles();
  const { segments } = useAdminGetSegments(ACTIVE_SEGMENTS_REQUEST);
  const segmentGroups = useMemo(() => groupSegmentsByLetter(segments), [segments]);

  return (
    <section className={classes.filters}>
      <TextField
        id={SEARCH_INPUT_ID}
        size="small"
        label="Rechercher"
        type="text"
        variant="outlined"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
      />
      <FormControl variant="outlined" size="small">
        <InputLabel htmlFor={TYPE_FILTER_INPUT_ID}>Type</InputLabel>
        <Select
          native
          label="Type"
          value={typeFilter}
          inputProps={{ id: TYPE_FILTER_INPUT_ID }}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            onTypeFilterChange(event.target.value as DeliveryPointTypeFilter)
          }
        >
          <option value={ALL_DELIVERY_POINT_TYPES}>Tous</option>
          {DELIVERY_POINT_TYPES.map((type) => (
            <option key={type} value={type}>
              {DELIVERY_POINT_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" size="small">
        <InputLabel htmlFor={SEGMENT_FILTER_INPUT_ID}>Segment</InputLabel>
        <Select
          native
          label="Segment"
          value={segmentFilter}
          inputProps={{ id: SEGMENT_FILTER_INPUT_ID }}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => onSegmentFilterChange(event.target.value)}
        >
          <option value={ALL_DELIVERY_POINT_SEGMENTS}>Tous</option>
          {segmentGroups.map(([letter, groupSegments]) => (
            <optgroup key={letter} label={letter}>
              {groupSegments.map((segment) => (
                <option key={segment.id} value={segment.id}>
                  {segment.name}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" size="small">
        <InputLabel htmlFor={STATUS_FILTER_INPUT_ID}>Statut</InputLabel>
        <Select
          native
          label="Statut"
          value={statusFilter}
          inputProps={{ id: STATUS_FILTER_INPUT_ID }}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
            onStatusFilterChange(event.target.value as EntityStatusFilter)
          }
        >
          {ENTITY_STATUS_FILTERS.map((status) => (
            <option key={status} value={status}>
              {ENTITY_STATUS_FILTER_LABELS[status]}
            </option>
          ))}
        </Select>
      </FormControl>
    </section>
  );
}
